import React, { JSX } from 'react';
import {
  ApiFilter,
  ApiFilterCategory,
  ApiFilterOption,
  ApiFilterType,
  ApiGroupedMultipleSelectionFilter,
  ApiMultipleSelectionFilter,
  ApiNumberRangeFilter,
  ApiSingleSelectionFilter
} from '@ibe/api';
import { slugify } from '@ibe/services';
import BaseLayoutFilter from '../../Components/DynamicList/FilterOptions/BaseLayoutFilter';
import FilterDropDown from '../../Components/DynamicList/FilterOptions/FilterDropDown';
import { FilterOptions } from '../../Components/DynamicList/FilterOptions';
import FilterRange from '../../Components/DynamicList/FilterOptions/FilterRange';
import fallback from '../../../_Translations/generated/service-list.en.t.json';
import Keys from '../../../_Translations/generated/service-list.en.t.json.keys';
import KeysFilterOption from '../../../_Translations/generated/filter-option.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import { FilterProperties, getIcon } from './externals';
import useConfig from '../../Hooks/useConfig';
import { FilterOverrides } from '../../Components/DynamicList/FilterOptions/FilterOptions';

const keysForRenderedFilters: { [key: string]: string } = {
  [ApiFilterType.SingleSelectionFilter]: 'SSF',
  [ApiFilterType.MultipleSelectionFilter]: 'MSF',
  [ApiFilterType.NumberRangeFilter]: 'NRF',
  [ApiFilterType.GroupedMultipleSelectionFilter]: 'GMSF'
};

const FilterComponent = ({
  f,
  filterOrdinalNumber,
  isCollapsable,
  filterProps,
  useIcons,
  filterOverrides,
  filterLabelTranslationMappings,
  showResultsCountInParenthesis,
  showResultsCount,
  showEmptyOptionsWhenFiltering,
  showResetButtonForFilter
}: {
  f: ApiFilter;
  filterOrdinalNumber: number;
  isCollapsable: boolean;
  filterProps?: FilterProperties;
  useIcons?: boolean;
  filterOverrides?: FilterOverrides;
  filterLabelTranslationMappings?: Record<string, (label: string) => string>;
  showResultsCountInParenthesis?: boolean;
  showResultsCount?: Partial<Record<ApiFilterCategory, boolean>>;
  showEmptyOptionsWhenFiltering?: boolean;
  showResetButtonForFilter?: boolean;
}): JSX.Element => {
  const { t } = useTranslation('service-list', fallback, Keys);
  const { t: tFilterOptions } = useTranslation('filter-option', fallback, KeysFilterOption);
  const { filterSortOption, filterSortOptionOrder } = useConfig();

  const filterType = f.type;
  const category = f.category || '';
  const { itemType } = f;
  const key = [
    keysForRenderedFilters[filterType as never],
    filterOrdinalNumber,
    slugify(category.toString())
  ].join('_');

  const onFilterSelectionChange = (
    selection: ApiFilterOption[],
    filter: ApiMultipleSelectionFilter
  ): void => {
    if (filterProps) {
      const changedFilter = {
        ...filter,
        selectedOptions: selection.length === 0 ? undefined : selection
      };
      filterProps.onFilterChange(changedFilter);
    }
  };

  const onFilterRangeChange = (min: number, max: number, filter: ApiNumberRangeFilter): void => {
    if (filterProps) {
      const changedFilter = { ...filter, minSelected: min, maxSelected: max };
      filterProps.onFilterChange(changedFilter);
    }
  };

  const onGroupedFilterSelectionChange = (
    selection: ApiFilterOption[],
    filter: ApiGroupedMultipleSelectionFilter
  ): void => {
    if (filterProps) {
      const changedFilter = {
        ...filter,
        selectedOptions: selection.length === 0 ? undefined : selection
      };
      filterProps.onFilterChange(changedFilter);
    }
  };

  const onSingleFilterSelectionChange = (
    selection: ApiFilterOption | undefined,
    filter: ApiSingleSelectionFilter
  ): void => {
    if (filterProps) {
      const changedFilter = { ...filter, selectedOption: selection };
      filterProps.onFilterChange(changedFilter);
    }
  };

  const sortByConfiguration = (
    identifier: string,
    filterOptions?: ApiFilterOption[]
  ): ApiFilterOption[] => {
    if (!filterOptions) return [];
    let options = [...filterOptions];
    if (!!filterLabelTranslationMappings?.[filterType]) {
      options = options.map(option => ({
        ...option,
        label: filterLabelTranslationMappings[filterType](option.label)
      }));
    } else {
      options = options.map(option => ({
        ...option,
        label: tFilterOptions(option.label, option.label)
      }));
    }

    if (filterSortOption) {
      const categoryIdentifier = itemType ? `${itemType}_${f.category}` : f.category;
      switch (filterType) {
        case ApiFilterType.SingleSelectionFilter: {
          if (
            filterSortOption.SingleSelectionFilter &&
            filterSortOption.SingleSelectionFilter[categoryIdentifier] === 'VALUE'
          ) {
            return sortAscOrDesc(
              options,
              filterSortOptionOrder?.SingleSelectionFilter?.[categoryIdentifier]
            );
          }
          break;
        }
        case ApiFilterType.GroupedMultipleSelectionFilter: {
          if (
            filterSortOption.GroupedMultipleSelectionFilter &&
            filterSortOption.GroupedMultipleSelectionFilter[categoryIdentifier] &&
            filterSortOption.GroupedMultipleSelectionFilter[categoryIdentifier][identifier] ===
              'VALUE'
          ) {
            return sortAscOrDesc(
              options,
              filterSortOptionOrder?.GroupedMultipleSelectionFilter?.[categoryIdentifier]
            );
          }
          break;
        }
        case ApiFilterType.MultipleSelectionFilter: {
          if (
            filterSortOption.MultipleSelectionFilter &&
            filterSortOption.MultipleSelectionFilter[categoryIdentifier] === 'VALUE'
          ) {
            return sortAscOrDesc(
              options,
              filterSortOptionOrder?.MultipleSelectionFilter?.[categoryIdentifier]
            );
          }
          break;
        }
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  };

  const sortAscOrDesc = (options: ApiFilterOption[], order?: 'ASC' | 'DESC' | 'DEFAULT') => {
    if (order === 'DEFAULT') {
      return options;
    } else if (order === 'DESC') {
      return options.sort((a, b) => b.value.localeCompare(a.value));
    } else {
      return options.sort((a, b) => a.value.localeCompare(b.value));
    }
  };

  switch (filterType) {
    case 'SingleSelectionFilter': {
      const singleSelectionFilter = f as ApiSingleSelectionFilter;
      return (
        <BaseLayoutFilter
          key={key}
          label={t(
            itemType
              ? `${itemType}_${singleSelectionFilter.category}`
              : singleSelectionFilter.category
          )}
          collapsable={isCollapsable}
          icon={getIcon(f, useIcons)}
          onDiscard={(): void => onSingleFilterSelectionChange(undefined, singleSelectionFilter)}
          resetLabel={t(Keys.RESET)}
          isResetButtonActive={showResetButtonForFilter}
        >
          <FilterDropDown
            options={sortByConfiguration('', singleSelectionFilter.options)}
            onChange={(selection): void =>
              onSingleFilterSelectionChange(selection, singleSelectionFilter)
            }
            selected={singleSelectionFilter.selectedOption}
            placeHolder={t(Keys.select)}
          />
        </BaseLayoutFilter>
      );
    }
    case 'GroupedMultipleSelectionFilter': {
      const groupMultiFilter = f as ApiGroupedMultipleSelectionFilter;
      const categoryGroupMultiFilter = groupMultiFilter.category;

      return (
        <>
          {Object.keys(groupMultiFilter.options || {})
            .sort((a, b) => a.localeCompare(b))
            .map(categoryKey => (
              <BaseLayoutFilter
                key={key + categoryKey}
                label={t(categoryKey)}
                collapsable={isCollapsable}
                icon={getIcon(f, useIcons)}
                onDiscard={(): void => onGroupedFilterSelectionChange([], groupMultiFilter)}
                resetLabel={t(Keys.RESET)}
                isResetButtonActive={showResetButtonForFilter}
              >
                <FilterOptions
                  options={sortByConfiguration(
                    categoryKey,
                    groupMultiFilter.options ? groupMultiFilter.options[categoryKey] : undefined
                  )}
                  onChange={(selection): void =>
                    onGroupedFilterSelectionChange(selection, groupMultiFilter)
                  }
                  selected={groupMultiFilter.selectedOptions || []}
                  showResultsCountInParenthesis={showResultsCountInParenthesis}
                  showResultsCount={showResultsCount?.[categoryGroupMultiFilter] ?? true}
                  showEmptyOptionsWhenFiltering={showEmptyOptionsWhenFiltering}
                />
              </BaseLayoutFilter>
            ))}
        </>
      );
    }
    case 'MultipleSelectionFilter': {
      const multiSelectFilter = f as ApiMultipleSelectionFilter;
      const categoryMultiSelectFilter = multiSelectFilter.category;
      return (
        <BaseLayoutFilter
          key={key}
          label={t(
            itemType ? `${itemType}_${multiSelectFilter.category}` : multiSelectFilter.category
          )}
          collapsable={isCollapsable}
          icon={getIcon(f, useIcons)}
          onDiscard={(): void => onFilterSelectionChange([], multiSelectFilter)}
          resetLabel={t(Keys.RESET)}
          isResetButtonActive={showResetButtonForFilter}
        >
          <FilterOptions
            options={sortByConfiguration('', multiSelectFilter.options)}
            onChange={(selection): void => onFilterSelectionChange(selection, multiSelectFilter)}
            selected={multiSelectFilter.selectedOptions || []}
            showResultsCountInParenthesis={showResultsCountInParenthesis}
            showResultsCount={showResultsCount?.[categoryMultiSelectFilter] ?? true}
            showEmptyOptionsWhenFiltering={showEmptyOptionsWhenFiltering}
            singleSelectCategories={
              multiSelectFilter.category === ApiFilterCategory.CATEGORY
                ? filterProps?.singleSelectCategories
                : false
            }
          />
        </BaseLayoutFilter>
      );
    }
    case 'NumberRangeFilter': {
      const numberRangeFilter = f as ApiNumberRangeFilter;
      return (
        <BaseLayoutFilter
          key={key}
          label={t(
            itemType ? `${itemType}_${numberRangeFilter.category}` : numberRangeFilter.category
          )}
          collapsable={isCollapsable}
          icon={getIcon(f, useIcons)}
          onDiscard={(): void =>
            onFilterRangeChange(numberRangeFilter.min, numberRangeFilter.max, numberRangeFilter)
          }
          resetLabel={t(Keys.RESET)}
          isResetButtonActive={showResetButtonForFilter}
        >
          <FilterRange
            filter={numberRangeFilter}
            triggerFilterChanged={(min, max, filter): void => onFilterRangeChange(min, max, filter)}
            filterOverrides={filterOverrides}
          />
        </BaseLayoutFilter>
      );
    }
    default:
      return <></>;
  }
};

export default FilterComponent;
